import { IStringTool } from "../core/IStringTool";
import { RuleParserResult } from "./RuleParserTool";

export interface ValidationContext {
  fieldKey?: string;
  values?: Record<string, any>;
  getValue?: (key: string) => any;
}

export interface ValidateInput {
  rules: RuleParserResult[];
  value: any;
  type?: "input" | "beforeinput";
  context?: ValidationContext;
}

export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

type ValidatorFn = (value: any, params?: string[], context?: ValidationContext) => boolean;

export class ValidationTool implements IStringTool<ValidateInput, ValidationResult> {
  process({ rules, value, type = "input", context }: ValidateInput): ValidationResult {
    const errors: string[] = [];
    for (const rule of rules) {
      var beforeInputValidatorFn: ValidatorFn | null = null;
      var inputValidatorFn: ValidatorFn | null = null;
      var inputValid = true;
      var beforeInputValid = true;

      if (type == "input") {
        inputValidatorFn = this.inputValidators[rule.key];
        if (!inputValidatorFn) continue;
      }
      if (type == "beforeinput") {
        beforeInputValidatorFn = this.beforeInputValidators[rule.key];
        if (!beforeInputValidatorFn) continue;
      }

      if (inputValidatorFn) {
        inputValid = inputValidatorFn(value, rule.params, context);
      }
      if (beforeInputValidatorFn) {
        beforeInputValid = beforeInputValidatorFn(value, rule.params, context);
      }

      if (!beforeInputValid) {
        errors.push(this.getErrorMessage(rule.key, rule.params));
      }
      if (!inputValid) {
        errors.push(this.getErrorMessage(rule.key, rule.params));
      }
    }
    return { valid: errors.length === 0, errors };
  }

  inputValidators: Record<string, ValidatorFn> = {
    required: (v) => v != null && String(v).trim() !== "",
    min: (v, params) => typeof v === "string" && v.length >= Number(params?.[0] ?? 0),
    max: (v, params) =>
      typeof v === "string" && v.length <= Number(params?.[0] ?? Number.MAX_SAFE_INTEGER),
    checked: (v) => {
      if (Array.isArray(v)) {
        return v.length > 0;
      }
      if (v === true || v === "true" || v === 1 || v === "1") {
        return true;
      }
      if (typeof v === "string") {
        const normalized = v.trim().toLowerCase();
        if (normalized === "false" || normalized === "0") {
          return false;
        }
        return normalized.length > 0;
      }
      return !!v;
    },
    email: (v) => typeof v === "string" && /^[^\s@]+@([a-z0-9-]+\.)+[a-z]{2,}$/i.test(v),
    phone: (v) => {
      if (typeof v !== "string") return false;
      const normalized = v.trim();
      if (normalized === "") return false;
      if (!/^[0-9()\s+-.]+$/.test(normalized)) return false;
      const digitCount = normalized.replace(/\D/g, "").length;
      return digitCount >= 7 && digitCount <= 15;
    },
    number: (v) => typeof v === "string" && /^-?\d+(\.\d+)?$/.test(v),
    integer: (v) => typeof v === "string" && /^-?\d+$/.test(v),
    url: (v) =>
      typeof v === "string" &&
      /^(https?:\/\/)?([\w\-]+\.)+[\w\-]+(\/[\w\-._~:\/?#[\]@!$&'()*+,;=]*)?$/.test(v),
    regex: (v, params) => this.testByRegex(v, params?.[0]),
    alpha: (v) => this.testByRegex(v, "^[A-Za-z]+$", true),
    alpha_num: (v) => this.testByRegex(v, "^[A-Za-z0-9]+$", true),
    alpha_dash: (v) => this.testByRegex(v, "^[A-Za-z0-9_-]+$", true),
    same: (v, params, context) => {
      const targetKey = params?.[0];
      const otherValue = this.getContextValue(context, targetKey);
      if (targetKey && otherValue === undefined) return false;
      return this.areValuesEqual(v, otherValue);
    },
    different: (v, params, context) => {
      const targetKey = params?.[0];
      const otherValue = this.getContextValue(context, targetKey);
      if (targetKey && otherValue === undefined) return false;
      return !this.areValuesEqual(v, otherValue);
    },
    range: (v, params) => {
      if (v == null || v === "") return true;
      const value = Number(v);
      const min = Number(params?.[0]);
      const max = Number(params?.[1]);
      if (Number.isNaN(value) || Number.isNaN(min) || Number.isNaN(max)) {
        return false;
      }
      return value >= min && value <= max;
    },
    digits: (v, params) => {
      if (typeof v !== "string") return false;
      const length = Number(params?.[0] ?? 0);
      if (length <= 0) return false;
      return new RegExp(`^\\d{${length}}$`).test(v);
    },
    ip: (v) => typeof v === "string" && (this.isIPv4(v) || this.isIPv6(v)),
    mimes: (v, params) => this.validateMimes(v, params),
    max_size: (v, params) => {
      const limit = Number(params?.[0]);
      if (!limit || limit <= 0) return true;
      return this.validateMaxSize(v, limit);
    },
    after: (v, params, context) => this.compareDates(v, params, context, "after"),
    before: (v, params, context) => this.compareDates(v, params, context, "before"),
  };

  beforeInputValidators: Record<string, ValidatorFn> = {
    number: (v) => /^-?\d*\.?\d*$/.test(v),
    integer: (v) => /^-?\d*$/.test(v),
    email: (v) => /^[\w@.\-+]*$/.test(v),
    phone: (v) => /^[0-9()\s+-.]*$/.test(v),
    letters: (v) => /^[a-zA-Z]*$/.test(v),
    lettersSpaces: (v) => /^[a-zA-Z\s]*$/.test(v),
    lettersNumbers: (v) => /^[a-zA-Z0-9]*$/.test(v),
    alpha: (v) => /^[A-Za-z]*$/.test(v),
    alpha_num: (v) => /^[A-Za-z0-9]*$/.test(v),
    alpha_dash: (v) => /^[A-Za-z0-9_-]*$/.test(v),
    digits: (v, params) => {
      const length = Number(params?.[0] ?? 0);
      if (length <= 0) return /^\d*$/.test(v);
      return new RegExp(`^\\d{0,${length}}$`).test(v);
    },
    url: (v) => /^[a-zA-Z0-9\-._~:\/?#\[\]@!$&'()*+,;=%]*$/.test(v),
    pattern: (v, params) => {
      try {
        return new RegExp(params?.[0] || "").test(v);
      } catch {
        return true;
      }
    },
  };

  getErrorMessage(key: string, params?: string[]): string {
    switch (key) {
      case "required":
        return "This field is required";
      case "email":
        return "Invalid email address";
      case "min":
        return `Minimum ${params?.[0]} characters`;
      case "max":
        return `Maximum ${params?.[0]} characters`;
      case "phone":
        return "Invalid phone number";
      case "number":
        return "Only numbers are allowed";
      case "integer":
        return "Only whole numbers are allowed";
      case "url":
        return "Invalid URL address";
      case "checked":
        return "You must accept";
      case "regex":
        return "Value does not match the required pattern";
      case "alpha":
        return "Only letters are allowed";
      case "alpha_num":
        return "Only letters and numbers are allowed";
      case "alpha_dash":
        return "Only letters, numbers, dashes, and underscores are allowed";
      case "same":
        return "Values do not match";
      case "different":
        return "Values must be different";
      case "range":
        return `Value must be between ${params?.[0]} and ${params?.[1]}`;
      case "digits":
        return `Value must contain exactly ${params?.[0]} digits`;
      case "ip":
        return "Invalid IP address";
      case "mimes":
        return `Allowed file types: ${params?.join(", ")}`;
      case "max_size":
        return `File must be smaller than ${params?.[0]} KB`;
      case "after":
        return `Date must be after ${params?.[0]}`;
      case "before":
        return `Date must be before ${params?.[0]}`;
      default:
        return "Invalid value";
    }
  }

  private validateMimes(value: any, params?: string[]): boolean {
    if (!params || params.length === 0) return true;
    const files = this.extractFiles(value);
    if (files.length === 0) return true;
    const allowed = params.map((p) => p.trim().toLowerCase());
    return files.every((file) => this.isMimeAllowed(file, allowed));
  }

  private validateMaxSize(value: any, limitKb: number): boolean {
    const files = this.extractFiles(value);
    if (files.length === 0) return true;
    const maxBytes = limitKb * 1024;
    return files.every((file) => {
      if (typeof file.size !== "number") return true;
      return file.size <= maxBytes;
    });
  }

  private extractFiles(value: any): Array<{ name?: string; size?: number; type?: string }> {
    if (!value) return [];
    const files: Array<{ name?: string; size?: number; type?: string }> = [];

    if (typeof File !== "undefined" && value instanceof File) {
      files.push(value);
      return files;
    }

    if (typeof FileList !== "undefined" && value instanceof FileList) {
      return Array.from(value);
    }

    if (Array.isArray(value)) {
      value.forEach((item) => {
        files.push(...this.extractFiles(item));
      });
      return files;
    }

    if (typeof value === "object" && ("name" in value || "size" in value || "type" in value)) {
      files.push(value as any);
      return files;
    }

    if (typeof value === "string" && value !== "") {
      files.push({ name: value });
    }

    return files;
  }

  private isMimeAllowed(file: { name?: string; type?: string }, allowed: string[]): boolean {
    const mime = (file.type || "").toLowerCase();
    const ext = this.getFileExtension(file.name);
    return allowed.some((rule) => {
      const normalized = rule.replace(/^\./, "").toLowerCase();
      if (!normalized) return false;
      if (normalized.includes("/")) {
        return mime === normalized;
      }
      return ext === normalized;
    });
  }

  private getFileExtension(name?: string): string {
    if (!name) return "";
    const parts = name.split(".");
    if (parts.length <= 1) return "";
    return (parts.pop() || "").toLowerCase();
  }

  private compareDates(
    value: any,
    params: string[] | undefined,
    context: ValidationContext | undefined,
    mode: "before" | "after",
  ): boolean {
    if (value == null || value === "") return true;
    const target = params?.[0];
    if (!target) return true;
    const currentDate = this.toDate(value);
    const referenceDate = this.resolveDateReference(target, context);
    if (!currentDate || !referenceDate) return false;
    return mode === "after"
      ? currentDate.getTime() > referenceDate.getTime()
      : currentDate.getTime() < referenceDate.getTime();
  }

  private resolveDateReference(token: string, context?: ValidationContext): Date | null {
    const contextualValue = this.getContextValue(context, token);
    if (contextualValue !== undefined) {
      return this.toDate(contextualValue);
    }
    if (token.toLowerCase() === "now") {
      return new Date();
    }
    if (token.toLowerCase() === "today") {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return today;
    }
    return this.toDate(token);
  }

  private toDate(value: any): Date | null {
    if (value == null || value === "") return null;
    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value;
    }
    if (typeof value === "number") {
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? null : date;
    }
    if (typeof value === "string") {
      const timestamp = Date.parse(value);
      if (!Number.isNaN(timestamp)) {
        return new Date(timestamp);
      }
    }
    return null;
  }

  private testByRegex(value: any, pattern?: string, allowEmpty = false): boolean {
    if (pattern == null || pattern === "") return true;
    const stringValue = typeof value === "string" ? value : value == null ? "" : String(value);
    if (allowEmpty && stringValue === "") return true;
    try {
      const { source, flags } = this.normalizeRegex(pattern);
      return new RegExp(source, flags).test(stringValue);
    } catch {
      return true;
    }
  }

  private normalizeRegex(pattern: string): { source: string; flags: string } {
    const trimmed = pattern.trim();
    if (trimmed.startsWith("/") && trimmed.lastIndexOf("/") > 0) {
      const lastSlashIndex = trimmed.lastIndexOf("/");
      const source = trimmed.slice(1, lastSlashIndex);
      const flags = trimmed.slice(lastSlashIndex + 1);
      return { source, flags };
    }
    return { source: trimmed, flags: "" };
  }

  private getContextValue(context: ValidationContext | undefined, key?: string): any {
    if (!context || !key) return undefined;
    if (context.values && Object.prototype.hasOwnProperty.call(context.values, key)) {
      return context.values[key];
    }
    if (context.getValue) {
      return context.getValue(key);
    }
    return undefined;
  }

  private areValuesEqual(a: any, b: any): boolean {
    if (Array.isArray(a) || Array.isArray(b)) {
      return JSON.stringify(a) === JSON.stringify(b);
    }
    return a === b;
  }

  private isIPv4(value: string): boolean {
    const segments = value.split(".");
    if (segments.length !== 4) return false;
    return segments.every((segment) => {
      if (!/^\d+$/.test(segment)) return false;
      const num = Number(segment);
      return num >= 0 && num <= 255;
    });
  }

  private isIPv6(value: string): boolean {
    if (!value) return false;
    if (value === "::") return true;
    const parts = value.split("::");
    if (parts.length > 2) return false;

    const hextetPattern = /^[0-9a-fA-F]{1,4}$/;
    const segments = value.split(":");
    if (parts.length === 2) {
      return (
        segments.every((segment) => segment === "" || hextetPattern.test(segment)) &&
        segments.length <= 8
      );
    }
    return segments.length === 8 && segments.every((segment) => hextetPattern.test(segment));
  }
}
